import { useEffect, type FC } from 'react';

const SITE_URL = 'https://zaptick.ai';
const DEFAULT_OG_IMAGE = `${SITE_URL}/logo512.png`;
const JSON_LD_SCRIPT_ID = 'zaptick-jsonld';

type JsonLd = Record<string, unknown> | Array<Record<string, unknown>>;

interface SeoProps {
  title: string;
  description: string;
  robots?: string;
  canonicalPath?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  jsonLd?: JsonLd;
}

const getCanonicalUrl = (canonicalPath?: string): string | undefined => {
  if (!canonicalPath) {
    return undefined;
  }

  if (canonicalPath === '/') {
    return `${SITE_URL}/`;
  }

  const normalizedPath = canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`;
  return `${SITE_URL}${normalizedPath}`;
};

const setMetaTag = (key: string, content: string, attribute: 'name' | 'property') => {
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const setCanonicalTag = (href?: string) => {
  let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!href) {
    canonical?.remove();
    return;
  }

  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }

  canonical.setAttribute('href', href);
};

const setJsonLd = (jsonLd?: JsonLd) => {
  const existingScript = document.getElementById(JSON_LD_SCRIPT_ID);
  if (!jsonLd) {
    existingScript?.remove();
    return;
  }

  const script = existingScript ?? document.createElement('script');
  script.setAttribute('id', JSON_LD_SCRIPT_ID);
  script.setAttribute('type', 'application/ld+json');
  script.textContent = JSON.stringify(jsonLd);

  if (!existingScript) {
    document.head.appendChild(script);
  }
};

const Seo: FC<SeoProps> = ({
  title,
  description,
  robots = 'index,follow',
  canonicalPath,
  ogType = 'website',
  ogImage = DEFAULT_OG_IMAGE,
  jsonLd,
}) => {
  useEffect(() => {
    const canonicalUrl = getCanonicalUrl(canonicalPath);

    document.title = title;
    setMetaTag('description', description, 'name');
    setMetaTag('robots', robots, 'name');

    setMetaTag('og:title', title, 'property');
    setMetaTag('og:description', description, 'property');
    setMetaTag('og:type', ogType, 'property');
    setMetaTag('og:site_name', 'ZapTick AI', 'property');
    setMetaTag('og:image', ogImage, 'property');

    setMetaTag('twitter:card', 'summary_large_image', 'name');
    setMetaTag('twitter:title', title, 'name');
    setMetaTag('twitter:description', description, 'name');
    setMetaTag('twitter:image', ogImage, 'name');

    if (canonicalUrl) {
      setMetaTag('og:url', canonicalUrl, 'property');
    } else {
      const ogUrlTag = document.head.querySelector('meta[property="og:url"]');
      ogUrlTag?.remove();
    }

    setCanonicalTag(canonicalUrl);
    setJsonLd(jsonLd);
  }, [canonicalPath, description, jsonLd, ogImage, ogType, robots, title]);

  return null;
};

export default Seo;
